import React, { createContext, useContext, useState } from "react";
import NotificationModal from "../pages/components/general/modals/NotificationModal";

type ShowNotificationFunction = (
  title: string,
  subtitle: React.ReactNode,
  onClose?: () => void,
  errors?: string[],
  /**
   * Technical lines rendered behind a "Dev only" badge. Populated in the dev
   * environment only — see error-handling/userFacingError.ts.
   */
  devDetails?: string[],
) => void;

let globalShowNotification: ShowNotificationFunction | null = null;

export const showNotification: ShowNotificationFunction = (
  title,
  subtitle,
  onClose,
  errors,
  devDetails,
) => {
  if (globalShowNotification) {
    globalShowNotification(title, subtitle, onClose, errors, devDetails);
  } else {
    window.alert(`${title}\n\n${String(subtitle)}`);
    if (onClose) onClose();
  }
};

type NotificationModalContextType = {
  isOpen: boolean;
  title: string;
  subtitle: React.ReactNode;
  errors?: string[];
  devDetails?: string[];
  showNotificationModal: ShowNotificationFunction;
  hideNotificationModal: () => void;
};

const NotificationModalContext =
  createContext<NotificationModalContextType | null>(null);

export const NotificationModalProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState<React.ReactNode>("");
  const [onCloseCallback, setOnCloseCallback] = useState<
    (() => void) | undefined
  >(undefined);
  const [errors, setErrors] = useState<string[] | undefined>(undefined);
  const [devDetails, setDevDetails] = useState<string[] | undefined>(undefined);

  const showNotificationModal = (
    title: string,
    subtitle: React.ReactNode,
    onClose?: () => void,
    errors?: string[],
    devDetails?: string[],
  ) => {
    setTitle(title);
    setSubtitle(subtitle);
    setOnCloseCallback(() => onClose);
    setIsOpen(true);
    setErrors(errors);
    setDevDetails(devDetails);
  };

  const hideNotificationModal = () => {
    setIsOpen(false);
    if (onCloseCallback) {
      onCloseCallback();
    }
  };

  // Set up the global notification function when the component mounts
  globalShowNotification = showNotificationModal;

  return (
    <NotificationModalContext.Provider
      value={{
        isOpen,
        title,
        subtitle,
        errors,
        devDetails,
        showNotificationModal,
        hideNotificationModal,
      }}
    >
      {children}
      <NotificationModal
        isOpen={isOpen}
        onConfirm={hideNotificationModal}
        title={title}
        subtitle={subtitle}
        errors={errors}
        devDetails={devDetails}
      />
    </NotificationModalContext.Provider>
  );
};

export const useNotificationModal = () => {
  const context = useContext(NotificationModalContext);
  if (!context) {
    throw new Error(
      "useNotificationModal must be used within a NotificationModalProvider",
    );
  }
  return context;
};
